import React, { useMemo } from "react";

import { Theme, useTheme } from "@mui/material/styles";

import {
  FullWidthFieldContainer,
  FullWidthTextField,
  RowContainer,
  ShortFieldContainer,
  SingleItemRowContainer,
  getMuiLocalizationProviderAdapterName,
  logDebug,
  mergeProperties,
  packableDeepClone,
} from "packable-components";
import {
  OrderActionsProps,
  OrderItemAction,
  ShippingStatus,
} from "./Order.types";
import { Grid, Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import {
  DatePicker,
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/en"; // US
import "dayjs/locale/en-gb"; // UK
import "dayjs/locale/en-ca"; // CA

import OrderStatus from "./OrderStatus";

const getStyles = (theme: Theme) => ({
  OrderActionContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignContent: "center",
    alignItems: "center",
  },
  OrderActionContentShortTextField: {
    width: "240px",
  },
  PackableSalectableChip: {
    muiSize: "small",
  },
});

export const OrderPickupRequestedActions: React.FC<OrderActionsProps> = (
  props
): React.JSX.Element => {
  const { orderItemAction, onOrderItemActionUpdate, styles } = props;

  logDebug("OrderPickupRequestedActions PROPS");
  logDebug(props);

  const theme = useTheme();

  const defaultStyles = useMemo(() => getStyles(theme), [theme]);
  const effectiveStyles = useMemo(
    () =>
      styles === undefined
        ? defaultStyles
        : mergeProperties(defaultStyles, styles),
    [defaultStyles, styles]
  );

  logDebug("OrderPickupRequestedActions effectiveStyles");
  logDebug(effectiveStyles);

  const handleNoteValueChange = (event: any) => {
    let newOrderItemAction = packableDeepClone(orderItemAction);
    newOrderItemAction.note = event.target.value;
    onOrderItemActionUpdate(newOrderItemAction);
  };

  const onPickupDateChange = (event: any) => {
    if (event) {
      if (event instanceof dayjs) {
        const newValue = (event as Dayjs).toDate();
        let newOrderItemAction: OrderItemAction =
          packableDeepClone(orderItemAction);
        newOrderItemAction.actionDate = newValue;
        onOrderItemActionUpdate(newOrderItemAction);
      }
    }
  };

  const handleChange = (
    event: React.MouseEvent<HTMLElement>,
    selection: ShippingStatus
  ) => {
    let newOrderItemAction = packableDeepClone(orderItemAction);
    newOrderItemAction.shippingStatus = selection;
    onOrderItemActionUpdate(newOrderItemAction);
  };

  const minDate = dayjs();
  const maxDate = dayjs().add(7, "days");
  const startTime = dayjs().set("hour", 9).set("minute", 0); // 09:00 AM
  const endTime = dayjs().set("hour", 17).set("minute", 0); // 05:00 PM

  const children = [
    <ToggleButton
      value={ShippingStatus.PICKUP_REQUESTED}
      key={ShippingStatus.PICKUP_REQUESTED}
    >
      <OrderStatus
        status={ShippingStatus.PICKUP_REQUESTED}
        isAdmin={true}
        displayChipForZeroCount
        disabled={
          orderItemAction.shippingStatus !== ShippingStatus.PICKUP_REQUESTED
        }
        styles={effectiveStyles}
      />
    </ToggleButton>,
    <ToggleButton
      value={ShippingStatus.PICKUP_SCHEDULED}
      key={ShippingStatus.PICKUP_SCHEDULED}
    >
      <OrderStatus
        status={ShippingStatus.PICKUP_SCHEDULED}
        isAdmin={true}
        label="Schedule Pickup"
        disabled={
          orderItemAction.shippingStatus !== ShippingStatus.PICKUP_SCHEDULED
        }
        displayChipForZeroCount
        styles={effectiveStyles}
      />
    </ToggleButton>,
  ];

  const control = {
    value: orderItemAction.shippingStatus,
    onChange: handleChange,
    exclusive: true,
  };

  return (
    <>
      <Grid sx={effectiveStyles.OrderActionContainer}>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <ToggleButtonGroup size="small" {...control} aria-label="Small sizes">
            {children}
          </ToggleButtonGroup>
        </Stack>
        {orderItemAction.shippingStatus !== ShippingStatus.PICKUP_REQUESTED && (
          <>
            {orderItemAction.usePickupHour ? (
              <RowContainer styles={{ PackableRowContainer: { pt: 1 } }}>
                <ShortFieldContainer>
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale={getMuiLocalizationProviderAdapterName()}
                  >
                    <DatePicker
                      className="OrderActionContentShortTextField"
                      sx={effectiveStyles.OrderActionContentShortTextField}
                      label="Scheduled Pick up Date"
                      value={dayjs(orderItemAction.actionDate || new Date())}
                      onChange={onPickupDateChange}
                      minDate={minDate}
                      maxDate={maxDate}
                    />
                  </LocalizationProvider>
                </ShortFieldContainer>
                <ShortFieldContainer>
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale={getMuiLocalizationProviderAdapterName()}
                  >
                    <TimePicker
                      className="OrderActionContentShortTextField"
                      sx={effectiveStyles.OrderActionContentShortTextField}
                      label="Scheduled Pick up Time"
                      value={dayjs(orderItemAction.actionDate || new Date())}
                      onChange={onPickupDateChange}
                      minTime={startTime}
                      maxTime={endTime}
                    />
                  </LocalizationProvider>
                </ShortFieldContainer>
              </RowContainer>
            ) : (
              <SingleItemRowContainer>
                <FullWidthFieldContainer>
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale={getMuiLocalizationProviderAdapterName()}
                  >
                    <DatePicker
                      className="OrderActionContentShortTextField"
                      sx={effectiveStyles.OrderActionContentShortTextField}
                      label="Scheduled Pick up Date"
                      value={dayjs(orderItemAction.actionDate || new Date())}
                      onChange={onPickupDateChange}
                      minDate={minDate}
                      maxDate={maxDate}
                    />
                  </LocalizationProvider>
                </FullWidthFieldContainer>
              </SingleItemRowContainer>
            )}
            <SingleItemRowContainer>
              <FullWidthFieldContainer>
                <FullWidthTextField
                  id="note"
                  label="Note"
                  value={orderItemAction.note}
                  onChange={handleNoteValueChange}
                />
              </FullWidthFieldContainer>
            </SingleItemRowContainer>
          </>
        )}
      </Grid>
    </>
  );
};

export default OrderPickupRequestedActions;
